/**
 * The measured geometry of the Multaqa mark, and the timing of the pen that
 * writes it. The React side is in logo-loader.tsx and logo-loader-parts.tsx;
 * the keyframes that consume these numbers are `logo-write-*` in
 * @multaqa/tokens theme.css.
 *
 * Nothing here is estimated. Every number is read off the artwork in viewBox
 * units, or worked out from a number that was.
 */

/** Intrinsic pixel size of both mark files. The SVG viewBox must match them. */
export const VIEW_W = 450
export const VIEW_H = 386

/**
 * The whole mark in ONE pen stroke, written strictly left to right.
 *
 * The pen touches down on the bottom-left foot, climbs to the apex, drops to
 * the middle vee, climbs to the right apex, and drops to the bottom-right
 * foot. It never lifts and it never travels backwards.
 *
 * An earlier version drew the left leg downward, lifted, and returned to the
 * apex to draw the rest. That return is a jump from the left foot back up to
 * the top left, against the direction of the writing, and it is what made the
 * letter read as assembled rather than written.
 *
 * The five points, in viewBox units. The apex ink spans x 10..92, so the apex
 * centre is 51; the leg ink spans x 10..115, so the foot centre is 62. Both
 * feet run to y 386 so the round cap clears the baseline. The outer vee tip is
 * at (225, 300), and the right half mirrors the left about x 225.
 */
export const WRITE_PATH = 'M 62 386 L 51 10 L 225 300 L 399 10 L 388 386'

/** Length of WRITE_PATH, summed segment by segment and rounded up. */
export const WRITE_LENGTH = 1429

/**
 * The three corners of the stroke, as the `stroke-dashoffset` the pen holds
 * when it arrives at each one.
 *
 * The two legs are 376.16 units long and the two diagonals are 338.20, so the
 * pen is at 1429 - 376 = 1053 at the apex, at 1053 - 338 = 715 at the vee, and
 * at 715 - 338 = 376 at the right apex.
 *
 * theme.css puts a keyframe on each, because a hand does not hold one speed
 * through a corner. It slows to about a quarter of its cruising speed at a
 * reversal and accelerates out of it. A single ease across the whole path
 * draws all three corners at full speed, and that is what makes a written
 * letter look machine-made.
 */
export const WRITE_AT_APEX = 1053
export const WRITE_AT_VEE = 715
export const WRITE_AT_RIGHT_APEX = 376

/**
 * Where those three corners fall in the stroke's own time, as percentages.
 *
 * Each is the share of the path length behind it. Time proportional to
 * distance is what keeps the speed continuous ACROSS a corner rather than
 * jumping at it: every segment then has the same mean speed, so the curves on
 * either side of a corner can hand over at the same slope. The vee lands on
 * exactly 50% because the two halves of the mark are mirror images.
 *
 * theme.css carries the same three numbers, and a test checks them against the
 * path. Change them together.
 */
export const WRITE_STOPS = [26.33, 50, 73.67] as const

/**
 * Width of the masking stroke. The mark is an OUTLINED M, so the brush must
 * cover both contours of a limb, not a centre line: the left leg is 105 units
 * across and the diagonal limb is about 120 units across measured
 * horizontally, which is 103 across measured square to the diagonal. 136
 * covers the widest case with a small margin. A narrower brush leaves slivers
 * of the outline behind the writing front.
 */
export const WRITE_STROKE = 136

/**
 * How long the whole intro takes, in milliseconds.
 *
 *     0 - 800   the pen writes the mark, left to right, without lifting
 *   800 - 1140   the wordmark sweeps in from the gap beside the mark
 *  1140 - 1500   everything holds still
 *
 * The last stretch is a deliberate pause on the finished logo, so the screen
 * does not cut away the moment the mark lands.
 *
 * 800ms to write an M is about the speed a hand actually moves. An earlier
 * version took 1600ms, which reads as a deliberate demonstration of writing
 * rather than writing. Every part here is half of what it was; the keyframe
 * percentages did not change, so the rhythm of the corners is identical and
 * only the clock is faster.
 *
 * Both routers pass this as `defaultPendingMinMs`, so a pending screen stays
 * up for the whole intro. That means EVERY route change that shows a pending
 * screen takes at least this long. theme.css carries the parts; this is their
 * sum, and the two must change together.
 */
export const LOGO_WRITE_DURATION_MS = 1500
